import React, { useState, useRef, useCallback, useEffect } from 'react';
import api, { setAuthLogoutCallback } from './services/api';
import Sidebar from './components/Sidebar';
import InvoiceAnalysisModule from './components/InvoiceAnalysisModule';
import TemplateIntelligenceModule from './components/TemplateIntelligenceModule';
import BulkAnalysisModule from './components/BulkAnalysisModule';
import LoginPage from './components/auth/LoginPage';
import UserManagementModule from './components/auth/UserManagementModule';
import './App.css';

// ──────────────────────────────────────────────────────────────
// CONFIGURATION
// ──────────────────────────────────────────────────────────────

const MAX_HISTORY = parseInt(process.env.REACT_APP_MAX_HISTORY_ITEMS || '20');

// ──────────────────────────────────────────────────────────────
// MAIN APP COMPONENT
// ──────────────────────────────────────────────────────────────

/**
 * FraudLens Dashboard App
 * 
 * Architecture:
 * - Sidebar: Navigation only (no modules)
 * - MainContent: Dynamically renders active module
 * - Modules: InvoiceAnalysis, TemplateIntelligence (can extend)
 * 
 * State management:
 * - activeModule: Controls which module is displayed
 * - All module data flows through props
 */
export default function App() {
  // ─── AUTH STATE ───────────────────────────────────────────────────────────
  // Read from localStorage synchronously so there's no flash of the login page
  // for users who are already authenticated.
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fraudlens_user')); }
    catch { return null; }
  });

  // Connect the api.js 401 interceptor to React state (runs once on mount)
  useEffect(() => {
    setAuthLogoutCallback(() => setUser(null));
  }, []);

  const handleLoginSuccess = (userData) => {
    // Always land on Invoice Analysis after login, regardless of which module
    // was active when the previous session ended.
    setActiveModule('invoice-analysis');
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('fraudlens_auth_token');
    localStorage.removeItem('fraudlens_user');
    setUser(null);
  };

  // ─── MODULE NAVIGATION ───
  const [activeModule, setActiveModule] = useState('invoice-analysis');

  // ─── INVOICE ANALYSIS STATE ───
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeHistory, setActiveHistory] = useState(null);
  const [textMode, setTextMode] = useState(false);
  const [invoiceText, setInvoiceText] = useState('');
  const [metadataNotes, setMetadataNotes] = useState('');

  // ─── TEMPLATE INTELLIGENCE STATE ───
  const [apiStatus, setApiStatus] = useState(null);
  const [templateStats, setTemplateStats] = useState(null);

  // ─── RESPONSIVE STATE ───
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ─── REFS ───
  const fileInputRef = useRef();

  // ──────────────────────────────────────────────────────────────
  // TEMPLATE INTELLIGENCE OPERATIONS
  // ──────────────────────────────────────────────────────────────

  const refreshTemplateStats = useCallback(() => {
    api
      .get('/template-stats')
      .then(r => setTemplateStats(r.data.stats))
      .catch(() => setTemplateStats({ total_templates: 0, vendors: [] }));
  }, []);

  // ──────────────────────────────────────────────────────────────
  // INVOICE ANALYSIS OPERATIONS
  // ──────────────────────────────────────────────────────────────

  const simulate = () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 90) {
          clearInterval(interval);
          return 90;
        }
        return p + Math.random() * 15;
      });
    }, 300);
    return interval;
  };

  const analyze = async () => {
    if (textMode && !invoiceText.trim()) return;
    if (!textMode && !file) return;

    setLoading(true);
    setResult(null);
    const iv = simulate();

    try {
      let resp;
      if (textMode) {
        const fd = new FormData();
        fd.append('invoice_text', invoiceText);
        if (metadataNotes) fd.append('metadata_notes', metadataNotes);
        resp = await api.post('/analyze-text', fd);
        setProgress(100);
        const entry = { filename: 'Manual text input', result: resp.data.result };
        setResult(entry);
        setHistory(h => [entry, ...h.slice(0, MAX_HISTORY - 1)]);
        setActiveHistory(null);
      } else {
        const fd = new FormData();
        fd.append('file', file);
        resp = await api.post('/analyze', fd, {
          onUploadProgress: (e) => setProgress(Math.min(60, (e.loaded / e.total) * 60)),
        });
        setProgress(100);
        const entry = { filename: file.name, result: resp.data.result, preview };
        setResult(entry);
        setHistory(h => [entry, ...h.slice(0, MAX_HISTORY - 1)]);
        setActiveHistory(null);
      }
    } catch (e) {
      const errEntry = {
        filename: textMode ? 'Manual text' : file?.name,
        result: { error: e.response?.data?.detail || e.message }
      };
      setResult(errEntry);
    } finally {
      clearInterval(iv);
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────
  // INITIALIZATION
  // ──────────────────────────────────────────────────────────────

  React.useEffect(() => {
    api
      .get('/health')
      .then(() => setApiStatus('online'))
      .catch(() => setApiStatus('offline'));
    refreshTemplateStats();
  }, [refreshTemplateStats]);

  // Handle window resize for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Close mobile menu when resizing to desktop
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ──────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────

  const displayResult = activeHistory !== null ? history[activeHistory] : result;

  // ─── LOGIN GATE (after all hooks — valid per React rules) ─────────────────
  if (!user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app">
      {/* ═══ SIDEBAR: NAVIGATION ONLY ═══ */}
      <Sidebar
        activeModule={activeModule}
        onModuleChange={(module) => {
          setActiveModule(module);
          if (isMobile) setMobileMenuOpen(false);
        }}
        apiStatus={apiStatus}
        history={history}
        activeHistory={activeHistory}
        onHistorySelect={setActiveHistory}
        templateStats={templateStats}
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuClose={() => setMobileMenuOpen(false)}
        user={user}
        onLogout={handleLogout}
      />

      {/* ═══ MAIN CONTENT AREA: DYNAMIC MODULE RENDERING ═══ */}
      <main className="main">
        {/* Top bar */}
        <div className="topbar">
          {isMobile && (
            <button 
              className="topbar-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              ☰
            </button>
          )}
          <div className="topbar-title">
            {activeModule === 'invoice-analysis'
              ? 'Invoice Analysis'
              : activeModule === 'template-intelligence'
                ? 'Template Intelligence'
                : activeModule === 'bulk-analysis'
                  ? 'Bulk Analysis'
                  : 'User Management'}
          </div>
          <div className="topbar-right">
            <div className="topbar-stats">
              <div className="topbar-stat">
                <span className="topbar-stat-num">{history.length}</span>
                <span className="topbar-stat-lbl">Analyzed</span>
              </div>
              <div className="topbar-stat-divider" />
              <div className="topbar-stat">
                <span className="topbar-stat-num">{templateStats?.total_templates ?? '—'}</span>
                <span className="topbar-stat-lbl">Templates</span>
              </div>
              <div className="topbar-stat-divider" />
              <div className="topbar-stat">
                <span className={`topbar-stat-num status-${apiStatus || 'pending'}`}>
                  {apiStatus === 'online' ? 'Live' : apiStatus === 'offline' ? 'Offline' : '…'}
                </span>
                <span className="topbar-stat-lbl">API</span>
              </div>
            </div>
            <div className="topbar-badge">GST · PAN · OCR · Tampering · Template AI</div>
          </div>
        </div>

        {/* Module Rendering */}
        {activeModule === 'invoice-analysis' ? (
          <InvoiceAnalysisModule
            displayResult={displayResult}
            file={file}
            onFileChange={setFile}
            preview={preview}
            onPreviewChange={setPreview}
            dragging={dragging}
            onDraggingChange={setDragging}
            loading={loading}
            progress={progress}
            textMode={textMode}
            onTextModeChange={setTextMode}
            invoiceText={invoiceText}
            onInvoiceTextChange={setInvoiceText}
            metadataNotes={metadataNotes}
            onMetadataNamesChange={setMetadataNotes}
            onAnalyze={analyze}
            fileInputRef={fileInputRef}
          />
        ) : activeModule === 'template-intelligence' ? (
          <TemplateIntelligenceModule
            stats={templateStats}
            onRefreshStats={refreshTemplateStats}
          />
        ) : activeModule === 'bulk-analysis' ? (
          <BulkAnalysisModule />
        ) : activeModule === 'user-management' && user?.is_admin ? (
          <UserManagementModule />
        ) : null}
      </main>
    </div>
  );
}
